import React from "react";
import { Badge } from "reactstrap";
import { FormFieldConfig } from "Components/Common/CommonForm";
import { validations } from "validations/FormValidations";
import { parseLocalDate } from "helpers/constant";
import { formatDate, formatTime24Hour } from "common/service/CommonService";

export const operationOptions = [
  { label: "Add", value: "Add" },
  { label: "Update", value: "Update" },
];

export const getAuditFormFields = (
  moduleOptions: any[],
  userOptions: any[],
  recordOptions: any[],
  subModuleOptions: any[],
  fromDate?: any,
  selectedModule?: string,
  selectedSubModule?: string,
  subModuleLoading?: boolean,
  recordLoading?: boolean
): FormFieldConfig[] => [
  {
    name: "from",
    label: "From Date",
    type: "date",
    placeholder: "From Date",
  },
  {
    name: "to",
    label: "To Date",
    type: "date",
    placeholder: "To Date",
    optionsProps: {
      minDate: fromDate ? (parseLocalDate(fromDate) ?? undefined) : undefined,
    },
  },
  {
    name: "module",
    label: "Module",
    type: "select",
    options: moduleOptions,
    placeholder: "Select Module",
    required: true,
    rules: validations.auditLog.module,
  },
  {
    name: "submodule",
    label: "Sub Module",
    type: "select",
    options: subModuleOptions,
    placeholder: "Select Sub Module",
    required: true,
    disabled: !selectedModule || subModuleLoading,
    rules: validations.auditLog.submodule,
  },
  {
    name: "record",
    label: "Record",
    type: "select",
    options: recordOptions,
    placeholder: "Select Record",
    disabled: !selectedSubModule || recordLoading,
  },
  {
    name: "operation",
    label: "Operation",
    type: "select",
    options: operationOptions,
    placeholder: "Select Operation",
  },
  {
    name: "user",
    label: "User",
    type: "select",
    options: userOptions,
    placeholder: "Select User",
  },
];

export const userActivityOptions = [
  { label: "All", value: "All"},
  { label: "Login", value: "System Login" },
  { label: "Logout", value: "System Logout" },
];

export const getUserActivityFields = (
  fromDateValue?: any,
  isActivityRequired: boolean = false,
): FormFieldConfig[] => [
  {
    name: "fromdate",
    label: "From Date",
    type: "date",
    placeholder: "Select From Date",
    optionsProps: {
      maxDate: "today",
    },
  },
  {
    name: "todate",
    label: "To Date",
    type: "date",
    placeholder: "Select To Date",
    optionsProps: {
      minDate: fromDateValue
        ? (parseLocalDate(fromDateValue) ?? undefined)
        : undefined,
    },
  },
  {
    name: "useractivity",
    label: "Activity",
    type: "select",
    placeholder: "Select Activity",
    options: userActivityOptions,
    required: isActivityRequired,
    isClearable: false
  },
];

export const USER_ACTIVITY_FAILURE_MESSAGES: Record<string, string> = {
  WRONG_PASSWORD: "Invalid password entered",
  WRONG_PASSWORD_MAX_ATTEMPTS_REACHED: "Maximum login attempts reached",
  USER_BLOCKED: "User account is blocked",
  USER_INACTIVE: "User account is inactive",
  INVALID_IP: "Login attempted from unauthorized IP address",
  ROLE_NOT_ASSIGNED: "No role or rights assigned to user",
  SESSION_TIMEOUT: "Session automatically terminated due to inactivity timeout",
  USER_SESSION_ACTIVE: "Last session was active",
  USER_NOT_FOUND: "User not found",
};

export const getUserActivityColumns = (currentPageNumber: number = 1): any[] => [
  {
    key: "srno",
    label: "SR NO",
    textCenter: true,
    render: (_: any, __: any, index: number) => (
      <span>{(currentPageNumber - 1) * 10 + index + 1}</span>
    ),
  },
  {
    key: "username",
    label: "USER NAME",
    render: (val: string) => (
      <span className="fw-semibold text-dark">{val || "-"}</span>
    ),
  },
  {
    key: "useractivity",
    label: "USER ACTIVITY",
    render: (val: string) => <span>{val || "-"}</span>,
  },
  {
    key: "activitydate",
    label: "ACTIVITY DATE",
    render: (_: any, row: any) => {
      const val = row.useractivitydate;
      if (!val) return <span>-</span>;
      return <span>{formatDate(val)}</span>;
    },
  },
  {
    key: "activitytime",
    label: "ACTIVITY TIME",
    render: (_: any, row: any) => {
      const val = row.useractivitydate;
      if (!val) return <span>-</span>;
      return <span>{formatTime24Hour(val, true)}</span>;
    },
  },
  {
    key: "statustext",
    label: "STATUS",
    textCenter: true,
    render: (_: any, row: any) => {
      const isSuccess =
        row?.issuccess || row?.statustext?.toLowerCase() === "success";
      const text = row?.statustext || (isSuccess ? "Success" : "Failed");
      return (
        <Badge
          color={isSuccess ? "success" : "danger"}
          className="fw-medium py-1.5"
          style={{
            borderRadius: "6px",
            fontSize: "12px",
            minWidth: "75px",
            display: "inline-block",
            textAlign: "center",
          }}
        >
          {text}
        </Badge>
      );
    },
  },
  {
    key: "useractivityfailurereason",
    label: "FAILURE REASON",
    render: (val: string | null) => {
      if (!val) return <span className="text-muted">-</span>;
      const displayReason = USER_ACTIVITY_FAILURE_MESSAGES[val] || val;
      return <span className="text-danger fw-medium">{displayReason}</span>;
    },
  },
];
